@if (asset()) {
<div class="flex flex-col relative">
  
  <!-- Sticky Header Section -->
  <div class="sticky top-0 z-20 bg-card border-b border-border pb-4 pt-4 px-4 shadow-sm flex flex-col gap-4">
    <!-- Header / Photo -->
    <div class="flex items-center gap-4">
      <div class="w-16 h-16 rounded-xl bg-surface border border-border flex items-center justify-center shrink-0">
        @if (asset()?.photoUrl) {
          <img [src]="asset()?.photoUrl" class="w-16 h-16 rounded-xl object-cover" alt="Vehicle photo">
        } @else {
          <lucide-icon [name]="getTypeIcon(asset()!.type)" class="w-8 h-8 text-muted"></lucide-icon>
        }
      </div>
      <div class="flex flex-col flex-1">
        <div class="flex items-center justify-between">
          <app-heading level="h3">{{ asset()?.unitNumber || 'No Unit #' }}</app-heading>
          <app-badge 
            [title]="getStatusLabel(asset()!.status)" 
            [variant]="getStatusBadgeVariant(asset()!.status)"
            badgeStyle="soft"
          ></app-badge>
        </div>
        <div class="flex items-center gap-2 mt-1">
          <app-badge 
            [title]="getAssetTypeLabel(asset())" 
            [variant]="getTypeBadgeVariant(asset()!.type)"
            [prefixIcon]="getTypeIcon(asset()!.type)"
            badgeStyle="soft"
          ></app-badge>
          @if (asset()?.year || asset()?.make || asset()?.model) {
            <app-text variant="muted" size="sm">
              {{ asset()?.year || '' }} {{ asset()?.make || '' }} {{ asset()?.model || '' }}
            </app-text>
          }
        </div>
      </div>
    </div>

    <!-- Missing info warnings -->
    @if (perms.canManageFleet() && incompleteReasons().length > 0) {
      <div
        animate.enter="hv-banner-enter"
        animate.leave="hv-banner-leave"
        class="p-3 bg-warning-low-contrast border border-warning/20 rounded-lg"
      >
        <div class="flex items-center gap-2 mb-2">
          <lucide-icon name="alert-triangle" class="w-4 h-4 text-warning"></lucide-icon>
          <span class="text-sm font-medium text-warning">Needs Attention</span>
        </div>
        <ul class="list-disc list-inside text-sm text-warning space-y-1 pl-1">
          @for (reason of incompleteReasons(); track reason) {
            <li>{{ reason }}</li>
          }
        </ul>
        <app-button variant="link" size="sm" class="mt-2 text-warning p-0 h-auto" (click)="editAsset()">
          Complete details
        </app-button>
      </div>
    }

    <!-- Actions -->
    <div class="flex gap-2">
      @if (perms.canManageFleet()) {
        <app-button variant="secondary" size="sm" class="flex-1" icon="pencil" (click)="editAsset()">
          Edit
        </app-button>
      }
      @if (perms.canChangeVehicleStatus()) {
        <app-button variant="secondary" size="sm" class="flex-1" icon="activity" (click)="changeStatus()">
          Status
        </app-button>
      }
    </div>
  </div>

  <!-- Scrollable Details Section -->
  <div class="flex flex-col gap-4 p-4">

  <hr class="border-border my-2">

  <!-- Identifiers -->
  <div class="space-y-4">
    <app-heading level="h4" class="text-sm font-semibold tracking-wide text-muted uppercase">Identifiers</app-heading>
    
    <div class="grid grid-cols-2 gap-4">
      <div>
        <app-text variant="muted" size="xs">VIN</app-text>
        <div class="font-mono text-sm mt-0.5 break-all">{{ asset()?.vin || 'Not specified' }}</div>
      </div>
      <div>
        <app-text variant="muted" size="xs">License Plate</app-text>
        <div class="font-medium text-sm mt-0.5">
          {{ asset()?.licensePlate || 'Not specified' }}
          @if (asset()?.licensePlateState) {
            <span class="text-muted ml-1">({{ asset()?.licensePlateState }})</span>
          }
        </div>
      </div>
    </div>
  </div>

  <hr class="border-border my-2">

  <!-- Specifications -->
  <div class="space-y-4">
    <app-heading level="h4" class="text-sm font-semibold tracking-wide text-muted uppercase">Specifications</app-heading>
    
    <div class="grid grid-cols-2 gap-y-4 gap-x-2">
      <div>
        <app-text variant="muted" size="xs">Body Class</app-text>
        <div class="text-sm mt-0.5">{{ asset()?.specs?.bodyClass || '—' }}</div>
      </div>
      <div>
        <app-text variant="muted" size="xs">Engine</app-text>
        <div class="text-sm mt-0.5">{{ asset()?.specs?.engineType || '—' }}</div>
      </div>
      <div>
        <app-text variant="muted" size="xs">GVWR</app-text>
        <div class="text-sm mt-0.5">{{ asset()?.specs?.gvwr || '—' }}</div>
      </div>
      <div>
        <app-text variant="muted" size="xs">Color</app-text>
        <div class="text-sm mt-0.5">{{ asset()?.color || '—' }}</div>
      </div>
      @if (asset()?.type === 'TRAILER') {
        <div>
          <app-text variant="muted" size="xs">Trailer Type</app-text>
          <div class="text-sm mt-0.5">{{ asset()?.specs?.trailerType || '—' }}</div>
        </div>
        <div>
          <app-text variant="muted" size="xs">Length</app-text>
          <div class="text-sm mt-0.5">{{ asset()?.specs?.trailerLengthFt ? asset()?.specs?.trailerLengthFt + ' ft' : '—' }}</div>
        </div>
        @if (asset()?.specs?.trailerType === 'Reefer') {
          <div class="col-span-2 mt-2 p-3 bg-surface/50 rounded-lg border border-border">
            <app-text variant="muted" size="xs" class="block mb-2 font-medium">Reefer Unit Information</app-text>
            <div class="grid grid-cols-3 gap-2">
              <div>
                <app-text variant="muted" size="xs">Make</app-text>
                <div class="text-sm">{{ asset()?.specs?.reeferMake || '—' }}</div>
              </div>
              <div>
                <app-text variant="muted" size="xs">Model</app-text>
                <div class="text-sm">{{ asset()?.specs?.reeferModel || '—' }}</div>
              </div>
              <div>
                <app-text variant="muted" size="xs">Year</app-text>
                <div class="text-sm">{{ asset()?.specs?.reeferYear || '—' }}</div>
              </div>
            </div>
          </div>
        }
      }
    </div>
  </div>

  <hr class="border-border my-2">

  <!-- Ownership -->
  <div class="space-y-4">
    <app-heading level="h4" class="text-sm font-semibold tracking-wide text-muted uppercase">Ownership</app-heading>
    
    <div>
      <app-text variant="muted" size="xs">Type</app-text>
      <div class="text-sm mt-0.5 capitalize">{{ asset()?.ownershipType?.toLowerCase() || 'Not specified' }}</div>
    </div>

    @if (asset()?.ownershipType === 'OWNED' && asset()?.ownedInfo) {
      <div class="grid grid-cols-2 gap-4">
        @if (asset()!.ownedInfo!.purchasePrice && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Purchase Price</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.ownedInfo!.purchasePrice | currency }}</div>
          </div>
        }
        @if (asset()!.ownedInfo!.purchaseDate) {
          <div>
            <app-text variant="muted" size="xs">Purchase Date</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.ownedInfo!.purchaseDate | date }}</div>
          </div>
        }
      </div>
    }

    @if (asset()?.ownershipType === 'LEASED' && asset()?.leaseInfo) {
      <div class="grid grid-cols-2 gap-4">
        @if (asset()!.leaseInfo!.lessor && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Lessor</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.leaseInfo!.lessor }}</div>
          </div>
        }
        @if (asset()!.leaseInfo!.monthlyPayment && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Monthly Payment</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.leaseInfo!.monthlyPayment | currency }}</div>
          </div>
        }
        @if (asset()!.leaseInfo!.leaseNumber) {
          <div class="col-span-2">
            <app-text variant="muted" size="xs">Lease Number</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.leaseInfo!.leaseNumber }}</div>
          </div>
        }
        @if (asset()!.leaseInfo!.startDate || asset()!.leaseInfo!.endDate) {
          <div class="col-span-2">
            <app-text variant="muted" size="xs">Lease Term</app-text>
            <div class="text-sm mt-0.5">
              {{ (asset()!.leaseInfo!.startDate | date) || '—' }} to {{ (asset()!.leaseInfo!.endDate | date) || '—' }}
            </div>
          </div>
        }
      </div>
    }

    @if (asset()?.ownershipType === 'FINANCED' && asset()?.financeInfo) {
      <div class="grid grid-cols-2 gap-4">
        @if (asset()!.financeInfo!.lender && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Lender</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.financeInfo!.lender }}</div>
          </div>
        }
        @if (asset()!.financeInfo!.loanAmount && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Loan Amount</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.financeInfo!.loanAmount | currency }}</div>
          </div>
        }
        @if (asset()!.financeInfo!.monthlyPayment && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Monthly Payment</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.financeInfo!.monthlyPayment | currency }}</div>
          </div>
        }
        @if (asset()!.financeInfo!.accountNumber && perms.canViewFleetPricing()) {
          <div>
            <app-text variant="muted" size="xs">Account Number</app-text>
            <div class="text-sm mt-0.5">{{ asset()!.financeInfo!.accountNumber }}</div>
          </div>
        }
        @if (asset()!.financeInfo!.startDate || asset()!.financeInfo!.endDate) {
          <div class="col-span-2">
            <app-text variant="muted" size="xs">Finance Term</app-text>
            <div class="text-sm mt-0.5">
              {{ (asset()!.financeInfo!.startDate | date) || '—' }} to {{ (asset()!.financeInfo!.endDate | date) || '—' }}
            </div>
          </div>
        }
      </div>
    }
  </div>

  <hr class="border-border my-2">

  <!-- Documents -->
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <app-heading level="h4" class="text-sm font-semibold tracking-wide text-muted uppercase">Documents</app-heading>
      @if (perms.canManageFleet()) {
        <app-button variant="link" size="sm" class="p-0 h-auto text-primary" icon="plus" (click)="uploadDocument()">
          Add Document
        </app-button>
      }
    </div>

    @if (visibleDocuments().length) {
      <div class="flex flex-col gap-2">
        @for (doc of visibleDocuments(); track doc.id) {
          <div 
            class="flex items-center gap-3 p-3 rounded-lg border border-border bg-surface hover:bg-card hover:border-primary/30 transition-colors cursor-pointer group"
            (click)="viewDocument(doc.filePath, 'pdf', doc.title + '.pdf')"
            (keydown.enter)="viewDocument(doc.filePath, 'pdf', doc.title + '.pdf')"
            tabindex="0"
          >
            <div class="w-10 h-10 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <lucide-icon name="file-text" size="20"></lucide-icon>
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate group-hover:text-primary transition-colors">{{ doc.title }}</span>
              <span class="text-xs text-muted">{{ doc.uploadedAt | date:'mediumDate' }}</span>
            </div>
            <lucide-icon name="external-link" size="16" class="text-muted group-hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"></lucide-icon>
          </div>
        }
      </div>
    } @else {
      <div class="p-4 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-center gap-2 bg-surface/50">
        <lucide-icon name="folder-open" class="w-8 h-8 text-muted"></lucide-icon>
        <app-text variant="muted" size="sm">No documents uploaded yet</app-text>
      </div>
    }
  </div>

  <hr class="border-border my-2">

  <!-- Photos -->
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <app-heading level="h4" class="text-sm font-semibold tracking-wide text-muted uppercase">Photos</app-heading>
      @if (perms.canManageFleet()) {
        <app-button variant="link" size="sm" class="p-0 h-auto text-primary" icon="plus" (click)="uploadPhoto()">
          Add Photo
        </app-button>
      }
    </div>

    @if (asset()?.photos?.length) {
      <div class="grid grid-cols-2 gap-3">
        @for (photo of asset()!.photos; track photo.id) {
          <div 
            class="relative aspect-square rounded-lg border border-border overflow-hidden group cursor-pointer"
            (click)="viewDocument(photo.filePath, 'image', photo.side + ' View.jpg')"
            (keydown.enter)="viewDocument(photo.filePath, 'image', photo.side + ' View.jpg')"
            tabindex="0"
          >
            <img [src]="photo.filePath" [alt]="photo.side + ' view'" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">
            <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
              <span class="text-xs font-medium text-white shadow-sm">{{ photo.side }}</span>
            </div>
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <lucide-icon name="zoom-in" class="text-white opacity-0 group-hover:opacity-100 transition-opacity" size="24"></lucide-icon>
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="p-4 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-center gap-2 bg-surface/50">
        <lucide-icon name="image" class="w-8 h-8 text-muted"></lucide-icon>
        <app-text variant="muted" size="sm">No photos uploaded yet</app-text>
      </div>
    }
  </div>
</div>
</div>
}
